// originally taken from: https://github.com/rcorp/css-schema/blob/master/cssschema.json

import {JSONSchema7} from "json-schema";

export const makeCssSchemaDefinitions = (): {"mui-container": JSONSchema7, sx: JSONSchema7, sx_breakpoints: JSONSchema7, css: JSONSchema7} => {
  return {
    "mui-container": {
      anyOf: [
        {$ref: "#/definitions/sx_breakpoints"},
        {
          type: "object",
          additionalProperties: true,
          properties: {
            "sx": {$ref: "#/definitions/sx"},
            "className": {type: "string"},
            "spacing": {
              oneOf: [
                {type: "integer", default: 2},
                {$ref: "#/definitions/sx_breakpoints"}
              ]
            },
            "rowSpacing": {
              oneOf: [
                {type: "integer", default: 2},
                {$ref: "#/definitions/sx_breakpoints"}
              ]
            },
            "columnSpacing": {
              oneOf: [
                {type: "integer", default: 2},
                {$ref: "#/definitions/sx_breakpoints"}
              ]
            },
            "columns": {
              oneOf: [
                {type: "integer", default: 2},
                {$ref: "#/definitions/sx_breakpoints"}
              ]
            },
            "xsOffset": {
              oneOf: [
                {type: "integer", default: 1},
                {const: "auto"}
              ]
            },
            "smOffset": {
              oneOf: [
                {type: "integer", default: 1},
                {const: "auto"}
              ]
            },
            "mdOffset": {
              oneOf: [
                {type: "integer", default: 1},
                {const: "auto"}
              ]
            },
            "lgOffset": {
              oneOf: [
                {type: "integer", default: 1},
                {const: "auto"}
              ]
            },
            "xlOffset": {
              oneOf: [
                {type: "integer", default: 1},
                {const: "auto"}
              ]
            }
          }
        }
      ]
    },
    sx: {
      anyOf: [
        {$ref: "#/definitions/css"},
        {
          patternProperties: {
            "^&": {$ref: "#/definitions/sx"}
          }
        },
        {
          type: "object",
          additionalProperties: true
        }
      ]
    } as JSONSchema7,
    sx_breakpoints: {
      type: "object",
      additionalProperties: true,
      properties: {
        "xs": { oneOf: [{type: "integer", default: 12}, {type: "boolean", default: true}, {const: "auto"}]},
        "sm": { oneOf: [{type: "integer", default: 12}, {type: "boolean", default: true}, {const: "auto"}]},
        "md": { oneOf: [{type: "integer", default: 12}, {type: "boolean", default: true}, {const: "auto"}]},
        "lg": { oneOf: [{type: "integer", default: 12}, {type: "boolean", default: true}, {const: "auto"}]},
        "xl": { oneOf: [{type: "integer", default: 12}, {type: "boolean", default: true}, {const: "auto"}]},
      }
    },
    css: {
      ...makeCssSchema(),
      "length": {
        "type": "string",
      },
      "time": {
        "type": "string",
      },
      "overflow": {
        "type": "string",
        "enum": [
          "visible",
          "scroll",
          "hidden",
          "auto"
        ],
      }
    } as JSONSchema7
  }
}

export const makeCssSchema = (): JSONSchema7 => {
  return {
    "type": "object",
    "properties": {
      "display": {
        "type": "string",
        "enum": [
          "inline",
          "table-row-group",
          "table-row",
          "table-header-group",
          "table-footer-group",
          "table-column-group",
          "table-column",
          "table-cell",
          "table-caption",
          "table",
          "run-in",
          "none",
          "list-item",
          "inline-table",
          "inline-flex",
          "inline-block",
          "flex",
          "compact",
          "block"
        ],

      },
      "position": {
        "type": "string",
        "enum": [
          "static",
          "sticky",
          "relative",
          "page",
          "fixed",
          "center",
          "absolute"
        ],

      },
      "color": {
        "type": "string",
        "format": "color",
        "title": "color",
        "default": "#ffa500"
      },
      "background": {
        "type": "string"
      },
      "backgroundColor": {
        "type": "string",
        "format": "color",
        "title": "background-color",
        "default": "#ffa500"
      },
      "fontFamily": {
        "type": "string",
        "title": "font-family"
      },
      "float": {
        "type": "string",
        "enum": [
          "none",
          "right",
          "left"
        ],

      },
      "all": {
        "type": "string",
        "enum": [
          "initial",
          "unset",
          "inherit"
        ],

      },
      "animationDirection": {
        "type": "string",
        "enum": [
          "normal",
          "reverse",
          "alternate-reverse",
          "alternate"
        ],

      },
      "animationFillMode": {
        "type": "string",
        "enum": [
          "none",
          "forwards",
          "both",
          "backwards"
        ],

      },
      "animationPlayMode": {
        "type": "string",
        "enum": [
          "running",
          "paused"
        ],

      },
      "visibility": {
        "type": "string",
        "enum": [
          "visible",
          "hidden",
          "collapse"
        ],

      },
      "backfaceVisibility": {
        "type": "string",
        "enum": [
          "visible",
          "hidden"
        ],

      },
      "backgroundAttachment": {
        "type": "string",
        "enum": [
          "scroll",
          "local",
          "fixed"
        ],

      },
      "backgroundClip": {
        "type": "string",
        "enum": [
          "border-box",
          "padding-box",
          "content-box"
        ],

      },
      "backgroundOrigin": {
        "type": "string",
        "enum": [
          "padding-box",
          "content-box",
          "border-box"
        ],

      },
      "backgroundRepeat": {
        "type": "string",
        "enum": [
          "repeat",
          "space",
          "round",
          "repeat-y",
          "repeat-x",
          "repeat no-repeat",
          "no-repeat"
        ],

      },
      "border": {
        type: "string"
      },
      "borderRadius": {
        type: "string"
      },
      "borderCollapse": {
        "type": "string",
        "enum": [
          "separate",
          "collapse"
        ],

      },
      "borderImageRepeat": {
        "type": "string",
        "enum": [
          "stretch",
          "space",
          "round",
          "repeat"
        ],

      },
      "boxDecorationBreak": {
        "type": "string",
        "enum": [
          "slice",
          "clone"
        ],

      },
      "boxSizing": {
        "type": "string",
        "enum": [
          "content-box",
          "padding-box",
          "border-box"
        ],

      },
      "breakBefore": {
        "type": "string",
        "enum": [
          "auto",
          "right",
          "page",
          "left",
          "column",
          "avoid-page",
          "avoid-column",
          "avoid",
          "always"
        ],

      },
      "breakAfter": {
        "type": "string",
        "enum": [
          "auto",
          "right",
          "page",
          "left",
          "column",
          "avoid-page",
          "avoid-column",
          "avoid",
          "always"
        ],

      },
      "breakInside": {
        "type": "string",
        "enum": [
          "auto",
          "avoid-page",
          "avoid-column",
          "avoid"
        ],

      },
      "captionSide": {
        "type": "string",
        "enum": [
          "top",
          "bottom"
        ],

      },
      "clear": {
        "type": "string",
        "enum": [
          "none",
          "right",
          "left",
          "both"
        ],

      },
      "clearAfter": {
        "type": "string",
        "enum": [
          "none",
          "top",
          "start",
          "right",
          "outside",
          "left",
          "inside",
          "end",
          "descendants",
          "bottom",
          "both"
        ],


      },
      "columnFill": {
        "type": "string",
        "enum": [
          "balance",
          "auto"
        ],


      },
      "columnSpan": {
        "type": "string",
        "enum": [
          "none",
          "all"
        ],


      },
      "columnRuleStyle": {
        "type": "string",
        "enum": [
          "none",
          "solid",
          "ridge",
          "outset",
          "inset",
          "hidden",
          "groove",
          "double",
          "dotted",
          "dashed"
        ],


      },
      "emptyCells": {
        "type": "string",
        "enum": [
          "show",
          "hide"
        ],


      },
      "flexDirection": {
        "type": "string",
        "enum": [
          "row",
          "row-reverse",
          "column-reverse",
          "column"
        ],
      },
      "flexWrap": {
        "type": "string",
        "enum": [
          "nowrap",
          "wrap-reverse",
          "wrap"
        ],
      },
      "justifyContent": {
        "type": "string",
        "enum": [
          "flex-start",
          "space-between",
          "space-around",
          "flex-end",
          "center"
        ],


      },
      "alignItems": {
        "type": "string",
        "enum": [
          "stretch",
          "flex-start",
          "flex-end",
          "center",
          "baseline"
        ],


      },
      "alignSelf": {
        "type": "string",
        "enum": [
          "auto",
          "stretch",
          "flex-start",
          "flex-end",
          "center",
          "baseline"
        ],


      },
      "alignContent": {
        "type": "string",
        "enum": [
          "stretch",
          "space-between",
          "space-around",
          "flex-start",
          "flex-end",
          "center"
        ],


      },
      "fontStretch": {
        "type": "string",
        "enum": [
          "normal",
          "ultra-expanded",
          "ultra-condensed",
          "semi-expanded",
          "semi-condensed",
          "extra-expanded",
          "extra-condensed",
          "expanded",
          "condensed"
        ],


      },
      "fontSynthesis": {
        "type": "string",
        "enum": [
          "weight style",
          "weight",
          "style",
          "none"
        ],


      },
      "fontKerning": {
        "type": "string",
        "enum": [
          "auto",
          "normal",
          "none"
        ],


      },

      "fontVariant": {
        "type": "string",
        "enum": [
          "normal",
          "unicase",
          "titling-caps",
          "small-caps",
          "petite-caps",
          "all-small-caps",
          "all-petite-caps"
        ],


      },
      "fontVariantCaps": {
        "type": "string",
        "enum": [
          "normal",
          "unicase",
          "titling-caps",
          "small-caps",
          "petite-caps",
          "all-small-caps",
          "all-petite-caps"
        ],


      },
      "fontStyle": {
        "type": "string",
        "enum": [
          "normal",
          "oblique",
          "italic"
        ],

      },
      "fontWeight": {
        "type": "string",
        "enum": [
          "normal",
          "lighter",
          "bolder",
          "bold",
          "900",
          "800",
          "700",
          "600",
          "500",
          "400",
          "300",
          "200",
          "100"
        ],

      },
      "hangingPunctuation": {
        "type": "string",
        "enum": [
          "none",
          "last force-end",
          "last allow-end",
          "last",
          "force-end",
          "first force-end",
          "first allow-end",
          "first",
          "allow-end"
        ],

      },
      "hyphens": {
        "type": "string",
        "enum": [
          "manual",
          "none",
          "auto"
        ],

      },
      "imageResolution": {
        "type": "string",
        "enum": [
          "1dppx",
          "from-image"
        ],

      },
      "lineBreak": {
        "type": "string",
        "enum": [
          "auto",
          "strict",
          "normal",
          "loose"
        ],

      },
      "listStylePosition": {
        "type": "string",
        "enum": [
          "outside",
          "inside"
        ],

      },
      "listStyleType": {
        "type": "string",
        "enum": [
          "disc",
          "upper-roman",
          "upper-latin",
          "upper-alpha",
          "square",
          "none",
          "lower-roman",
          "lower-latin",
          "lower-greek",
          "lower-alpha",
          "georgian",
          "decimal-leading-zero",
          "decimal",
          "circle",
          "armenian"
        ],

      },
      "objectFit": {
        "type": "string",
        "enum": [
          "fill",
          "scale-down",
          "none",
          "cover",
          "contain"
        ],

      },
      "outlineStyle": {
        "type": "string",
        "enum": [
          "none",
          "solid",
          "ridge",
          "outset",
          "inset",
          "groove",
          "double",
          "dotted",
          "dashed",
          "auto"
        ],

      },

      "overflowWrap": {
        "type": "string",
        "enum": [
          "normal",
          "break-word"
        ],

      },
      "pageBreakAfter": {
        "type": "string",
        "enum": [
          "auto",
          "right",
          "left",
          "avoid",
          "always"
        ],

      },
      "pageBreakBefore": {
        "type": "string",
        "enum": [
          "auto",
          "right",
          "left",
          "avoid",
          "always"
        ],

      },
      "pageBreakInside": {
        "type": "string",
        "enum": [
          "auto",
          "avoid"
        ],

      },
      "pointerEvents": {
        "type": "string",
        "enum": [
          "auto",
          "none"
        ],

      },
      "resize": {
        "type": "string",
        "enum": [
          "none",
          "vertical",
          "horizontal",
          "alwbothays"
        ],

      },
      "tableLayout": {
        "type": "string",
        "enum": [
          "auto",
          "fixed"
        ],

      },
      "textAlignLast": {
        "type": "string",
        "enum": [
          "auto",
          "start",
          "right",
          "left",
          "justify",
          "end",
          "center"
        ],

      },
      "textDecorationLine": {
        "type": "string",
        "enum": [
          "none",
          "underline",
          "overline",
          "line-through",
          "blink"
        ],

      },
      "textDecorationSkip": {
        "type": "string",
        "enum": [
          "none",
          "spaces",
          "objects",
          "ink",
          "edges",
          "box-decoration"
        ],

      },
      "textDecorationPosition": {
        "type": "string",
        "enum": [
          "auto",
          "under right",
          "under left",
          "under",
          "right",
          "left"
        ],

      },
      "textDecorationStyle": {
        "type": "string",
        "enum": [
          "solid",
          "wavy",
          "double",
          "dotted",
          "dashed"
        ],

      },
      "textEmphasisPosition": {
        "type": "string",
        "enum": [
          "over right",
          "over left",
          "below right",
          "below left"
        ],

      },
      "textJustify": {
        "type": "string",
        "enum": [
          "auto",
          "none",
          "inter-word",
          "distribute"
        ],

      },
      "textOrientation": {
        "type": "string",
        "enum": [
          "mixed",
          "use-glyph-orientation",
          "upright",
          "sideways-right",
          "sideways-left",
          "sideways"
        ],


      },
      "transformStyle": {
        "type": "string",
        "enum": [
          "flat",
          "preserve-3d"
        ],


      },
      "textTransform": {
        "type": "string",
        "enum": [
          "none",
          "uppercase",
          "lowercase",
          "full-width",
          "capitalize"
        ],


      },
      "unicodeBidi": {
        "type": "string",
        "enum": [
          "normal",
          "embed",
          "bidi-override"
        ],


      },
      "whiteSpace": {
        "type": "string",
        "enum": [
          "normal",
          "pre-wrap",
          "pre-line",
          "pre",
          "nowrap"
        ],


      },
      "wordBreak": {
        "type": "string",
        "enum": [
          "normal",
          "keep-all",
          "break-all"
        ],


      },
      "wordWrap": {
        "type": "string",
        "enum": [
          "normal",
          "break-word"
        ],


      },
      "writingWode": {
        "type": "string",
        "enum": [
          "horizontal-tb",
          "vertical-rl",
          "vertical-lr"
        ],


      },
      "top": {
        "$ref": "#/definitions/css/length"
      },
      "left": {
        "$ref": "#/definitions/css/length"
      },
      "right": {
        "$ref": "#/definitions/css/length"
      },
      "bottom": {
        "$ref": "#/definitions/css/length"
      },
      "margin": {
        "$ref": "#/definitions/css/length"
      },
      "marginLeft": {
        "$ref": "#/definitions/css/length"
      },
      "marginRight": {
        "$ref": "#/definitions/css/length"
      },
      "marginTop": {
        "$ref": "#/definitions/css/length"
      },
      "marginBottom": {
        "$ref": "#/definitions/css/length"
      },
      "padding": {
        "$ref": "#/definitions/css/length"
      },
      "paddingRight": {
        "$ref": "#/definitions/css/length"
      },
      "paddingLeft": {
        "$ref": "#/definitions/css/length"
      },
      "paddingTop": {
        "$ref": "#/definitions/css/length"
      },
      "paddingBottom": {
        "$ref": "#/definitions/css/length"
      },
      "animationDelay": {
        "$ref": "#/definitions/css/time"
      },
      "animationDuration": {
        "$ref": "#/definitions/css/time"
      },
      "overflowX": {
        "$ref": "#/definitions/css/overflow"
      },
      "overflowY": {
        "$ref": "#/definitions/css/overflow"
      }

    }
  }
}
